/* eslint-disable max-len */
import React, { useMemo } from 'react';
import { ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS, applyLayoutCustomization, CustomizedEntityDetailsPageLayout, DEFAULT_ENTITY_DETAILS_LAYOUT,
    EntityDetailsCompsPlacement, EntityDetailsGenericLayoutContainer, EntityDetailsPageLayout, EntityUIComponentCollection, EntityUIComponentProps,
    isCustomizedEntityDetailsPageLayout, isEntityDetailsCompsPlacement, isEntityDetailsPageLayout, isValidComponentsPlacement } from '..';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral } from '../../../utils/utils';



/**
 * Interface for EntityContainerUIComponentCustomSettings's customSettings. These are the props/value that can be
 * configured for the 'EntityContainerUIComponentCustomSettings' from the entityClass's uiSettings.
 */
export interface EntityContainerUIComponentCustomSettings {
    // To specify the layout of the container. This can be a string or an EntityDetailsPageLayout object. If we want to use one of the existing
    // predefined layout e.g. 'default', 'double', 'single_double', etc... then we can specify the name of the predefined layout. If we want to
    // use a completely custom layout, we can provide the settings for the layout object, see the structure of the 'EntityDetailsPageLayout' for
    // more details on how to configure this object.
    // By default, the component will use the 'default' layout, the 1 column layout.
    readonly layout?: string | EntityDetailsPageLayout | undefined;

    // If we use one of the predefined layouts BUT wanted to customize it, we can use this option. This is optional and only needed if we
    // want to customize the predefined layout. For example, to use the 'single_double' layout BUT change the columns' width so that the left
    // column is half the size of the right column instead of 50-50 (the default), we can do this
    //      {
    //          layout: "single_double",
    //          layoutCustomization: {
    //              templateColumns: "1fr 2fr"
    //          }
    //      }
    readonly layoutCustomization?: CustomizedEntityDetailsPageLayout | undefined;

    // To specify where to place the components
    readonly componentsPlacement: EntityDetailsCompsPlacement;
}



/**
 * type guard function to check if an object is a type of EntityContainerUIComponentCustomSettings
 */
const isEntityContainerUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityContainerUIComponentCustomSettings => {

    const settings = obj as EntityContainerUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }


    if (settings.layout !== undefined) {
        if (typeof settings.layout !== 'string' && !isObjectLiteral(settings.layout)) {
            return errorLogger?.logError({
                message: '"layout" must be a string or an Object',
                value  : settings.layout,
                path   : [...configPath, 'layout'],
            }) ?? false;
        }

        // If "layout" is a string, make sure it is the name of one of the predefined layout e.g. "default", "double", "single_double",  etc...
        const predefinedLayoutNames = Object.keys(ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS);
        if (typeof settings.layout === 'string' && !ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS[settings.layout]) {
            return errorLogger?.logError({
                message: `"layout" value must be one of the following predefined layouts [${predefinedLayoutNames.map(
                    (name) => {
                        return `"${name}"`;
                    }).join(', ')}]`,
                value: settings.layout,
                path : [...configPath, 'layout'],
            }) ?? false;
        }

        // If "layout" is an object, make sure it has the correct settings
        if (isObjectLiteral(settings.layout) && !isEntityDetailsPageLayout(settings.layout, errorLogger, [...configPath, 'layout'])) {
            return false;
        }
    }

    if (
        settings.layoutCustomization !== undefined
        && !isCustomizedEntityDetailsPageLayout(settings.layoutCustomization, errorLogger, [...configPath, 'layoutCustomization'])
    ) {
        return false;
    }

    if (!isEntityDetailsCompsPlacement(settings.componentsPlacement, errorLogger, [...configPath, 'componentsPlacement'])) {
        return false;
    }

    // If "layout" is configured, make sure it matches the "componentsPlacement".
    const layoutObject = typeof settings.layout === 'string' ? ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS[settings.layout] : settings.layout;
    if (
        layoutObject
        && !isValidComponentsPlacement(layoutObject, settings.componentsPlacement, errorLogger, [...configPath, 'componentsPlacement'])
    ) {
        return false;
    }

    return true;
};



export interface EntityContainerUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityContainerUIComponentCustomSettings | undefined;
}


/**
 * This is a UI Component that can be used as a container for other components. It does not display entity data but only Entity UI Components.
 */
export const EntityContainerUIComponent: React.FC<EntityContainerUIComponentProps> = (
    props: EntityContainerUIComponentProps,
) => {

    // Get the actual layout object from the customSettings.
    const layoutObject = useMemo(() => {
        if (props.customSettings) {
            const layout = props.customSettings.layout ?? DEFAULT_ENTITY_DETAILS_LAYOUT;

            // Get the actual layout object from the "layout". If the "layout" is a string, assume it is a name of one of the predefined
            // layouts and look it up from the ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS. If it is an Object then assume it is a layout object and use it
            // as is.
            const layoutObject = typeof layout === 'string'
                ? ALL_PREDEFINED_ENTITY_DETAILS_LAYOUTS[layout] ?? DEFAULT_ENTITY_DETAILS_LAYOUT
                : layout;

            // Apply pageLayoutCustomization to the resultLayout before returning the final layout object
            return applyLayoutCustomization(layoutObject, props.customSettings.layoutCustomization);
        }
        return undefined;
    }, [props.customSettings]);


    return (
        <>
            {props.customSettings && layoutObject && (
                <EntityDetailsGenericLayoutContainer
                    projectId={props.projectId}
                    entityId={props.entityId}
                    pageLayout={layoutObject}
                    componentsPlacement={props.customSettings.componentsPlacement}
                    onShowEntityDetails={props.onShowEntityDetails}
                    onShowEntityPreview={props.onShowEntityPreview}
                    fetchDataParams={props.fetchDataParams}
                />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityContainerUIComponent', EntityContainerUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Container_Comp',
    {
        componentFunc          : EntityContainerUIComponent,
        customSettingsValidator: isEntityContainerUIComponentCustomSettings,
    },
);
